// The Connect stage machine (§s03, execution plan WS-3 step 1/3): `?stage=`
// is a query param on `/connect`, not a route, so a reload resumes. Stages:
// link -> exchanging -> first-sync -> success, plus a standalone error stage.
// `onExit` from Link with no token returns to the empty state with NO error
// framing -- the design is explicit about this (WS-3 step 1).
import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Skeleton } from '@moneyskyline/ui';
import { isErrorEnvelope } from '@/api/envelope';
import { HonestInline } from '@/components/HonestInline';
import type { Account } from '@/api/hooks/useAccounts';
import { usePlaidLinkToken } from '@/api/hooks/usePlaidLinkToken';
import { useExchangePublicToken } from '@/api/hooks/useExchangePublicToken';
import { useJob } from '@/api/hooks/useJob';
import { useUpdateComplete } from '@/api/hooks/useItemMutations';
import { useCurrentLinkReservation, useReportLinkExit } from '@/api/hooks/useLinkReservation';
import { FirstSyncPanel } from './FirstSyncPanel';
import { PlaidLinkBoundary } from './PlaidLinkBoundary';

export type ConnectStageName = 'link' | 'exchanging' | 'first-sync' | 'success' | 'error';

export interface ConnectStageProps {
  stage: ConnectStageName;
  /** Present for an update-mode (reconnect) Link. */
  itemId?: string | null;
  jobId?: string | null;
  exchangedAccounts: Account[] | null;
  onExchanged: (result: { itemId: string; jobId: string; accounts: Account[] }) => void;
  onSyncDone: (jobId: string) => void;
  onContinue: () => void;
  /** Link closed with no token, or the Link seam is not configured -- return to the empty state, no error framing. */
  onExit: () => void;
}

function VerifiedSuccess({ itemId, jobId, onContinue }: { itemId: string | null | undefined; jobId: string | null | undefined; onContinue: () => void }) {
  const jobQuery = useJob(jobId);
  if (!itemId || !jobId || jobQuery.isLoading) return <HonestInline kind="api-unavailable" />;
  if (jobQuery.isError || jobQuery.data?.status !== 'done' || jobQuery.data.params.item_id !== itemId) return <HonestInline kind="api-unavailable" />;
  return <div className="flex flex-col gap-3 rounded-lg border border-positive-border bg-positive-surface p-4" data-testid="connect-success-fallback"><h2 className="text-title text-positive-foreground">Connected</h2><p className="text-sm text-positive-foreground">Your connection finished syncing. Accounts are available on the Accounts page.</p><button type="button" onClick={onContinue} className="self-start rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Go to Accounts</button></div>;
}

/** "Link open": recovers an exchanged reservation, then waits for an explicit user click before minting Link. */
function LinkOpenPanel({
  itemId,
  onExchanged,
  onExit,
}: {
  itemId?: string | null;
  onExchanged: ConnectStageProps['onExchanged'];
  onExit: () => void;
}) {
  const linkTokenMutation = usePlaidLinkToken();
  const exchangeMutation = useExchangePublicToken();
  const updateCompleteMutation = useUpdateComplete();
  const currentReservationQuery = useCurrentLinkReservation();
  const reportExitMutation = useReportLinkExit();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const recoveryHandled = useRef(false);
  const startRequested = useRef(false);

  useEffect(() => {
    if (!currentReservationQuery.isSuccess) return;
    // Update mode belongs to the supplied owned Item. A previous fresh-link
    // reservation must never divert reconnect into an old bootstrap job.
    if (itemId) return;
    const current = currentReservationQuery.data;
    if (!recoveryHandled.current && current?.status === 'exchanged' && current.item_id && current.job_id) {
      recoveryHandled.current = true;
      onExchanged({ itemId: current.item_id, jobId: current.job_id, accounts: [] });
    }
  }, [currentReservationQuery.data, currentReservationQuery.isSuccess, itemId, onExchanged]);

  const blockedAttempt = !itemId && !linkToken && (currentReservationQuery.data?.status === 'reserved' || currentReservationQuery.data?.status === 'ambiguous');
  if (currentReservationQuery.isError || blockedAttempt || message || linkTokenMutation.isError || exchangeMutation.isError || updateCompleteMutation.isError) {
    return <HonestInline kind="api-unavailable" action={{ label: 'Back', onAction: onExit }} />;
  }

  if (linkToken) {
    return (
      <>
        <PlaidLinkBoundary
          linkToken={linkToken}
          mode={itemId ? 'update' : 'new'}
          onSuccess={({ publicToken, linkSessionId, institution }) => {
            if (itemId) {
              // Update mode deliberately discards the SDK public token.
              void publicToken;
              void institution;
              updateCompleteMutation.mutate(
                { itemId, linkSessionId },
                { onSuccess: (response) => onExchanged({ itemId: response.item_id, jobId: response.job_id, accounts: [] }) },
              );
              return;
            }
            if (!reservationId) {
              setMessage('The connection identity was unavailable. No retry was started.');
              return;
            }
            exchangeMutation.mutate(
              { public_token: publicToken, reservation_id: reservationId, link_session_id: linkSessionId, institution },
              { onSuccess: (response) => onExchanged({ itemId: response.item_id, jobId: response.job_id, accounts: [] }) },
            );
          }}
          onExit={(linkSessionId) => {
            if (reservationId) {
              reportExitMutation.mutate({ reservationId, linkSessionId }, { onSettled: onExit });
              return;
            }
            onExit();
          }}
          onError={(errorMessage, linkSessionId) => {
            if (reservationId && linkSessionId !== undefined) {
              reportExitMutation.mutate({ reservationId, linkSessionId });
            }
            setMessage(errorMessage);
          }}
        />
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4" data-testid="link-open-panel">
          <Skeleton className="h-4 w-48" />
          <p className="text-sm text-muted-foreground">Opening Plaid Link…</p>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4" data-testid="link-open-panel">
      <h2 className="text-title">{itemId ? 'Reconnect your bank' : 'Connect your bank'}</h2>
      <p className="text-sm text-muted-foreground">
        MoneySkyline helps you see your wealth and understand personal and business spending.
        Plaid reads balances and transactions from the accounts you share.
      </p>
      <button
        type="button"
        className="min-h-11 self-start rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
        onClick={() => {
          if (startRequested.current || !currentReservationQuery.isSuccess) return;
          startRequested.current = true;
          linkTokenMutation.mutate(itemId ?? null, {
            onSuccess: (response) => {
              if (response.mode === 'new') setReservationId(response.reservation_id);
              setLinkToken(response.link_token);
            },
            onError: (error: unknown) => {
              const code = isAxiosError(error) && isErrorEnvelope(error.response?.data)
                ? error.response.data.error.code
                : 'unknown';
              console.warn('link_token denied', { code, mode: itemId ? 'update' : 'new' });
              setMessage('Plaid Link could not be prepared. No bank connection was confirmed.');
            },
          });
        }}
        disabled={linkTokenMutation.isPending || !currentReservationQuery.isSuccess}
      >
        {linkTokenMutation.isPending ? 'Preparing secure connection…' : 'Continue to Plaid'}
      </button>
    </div>
  );
}

export function ConnectStage({
  stage,
  itemId,
  jobId,
  exchangedAccounts: _exchangedAccounts,
  onExchanged,
  onSyncDone,
  onContinue,
  onExit,
}: ConnectStageProps) {
  switch (stage) {
    case 'link':
      return <LinkOpenPanel itemId={itemId} onExchanged={onExchanged} onExit={onExit} />;

    case 'exchanging':
      return <LinkOpenPanel itemId={itemId} onExchanged={onExchanged} onExit={onExit} />;

    case 'first-sync':
      if (!jobId) {
        return <HonestInline kind="api-unavailable" action={{ label: 'Back to Accounts', onAction: onContinue }} />;
      }
      return <FirstSyncPanel jobId={jobId} itemId={itemId ?? null} onDone={() => onSyncDone(jobId)} />;

    case 'success':
      // A reload lost the in-memory exchange result. Never fabricate which
      // accounts were "new" -- fall back to an honest confirmation only.
      return <VerifiedSuccess itemId={itemId} jobId={jobId} onContinue={onContinue} />;

    case 'error':
    default:
      return <HonestInline kind="api-unavailable" action={{ label: 'Back to Accounts', onAction: onContinue }} />;
  }
}

export default ConnectStage;
